import { openai } from './client';
import { PaymentData } from '../../../types';
import { cleanInvoiceData } from './utils';
import { extractInvoiceDataWithRegex } from '../invoice/regex';
import { logOpenAIInteraction } from './logger';

export async function extractInvoiceData(text: string): Promise<PaymentData> {
  if (!text || typeof text !== 'string') {
    return extractInvoiceDataWithRegex(String(text || ''));
  }

  try {
    const messages = [{
      role: "system" as const,
      content: `You are an expert at extracting payment information from invoices. Extract ONLY the following fields and return them in JSON format:

Required fields:
- name: The company or person sending the invoice (usually at the top)
- iban: The IBAN number (format: 2 letters followed by numbers, e.g., BE68539007547034)
- amount: The total amount to pay (look for terms like "total", "amount due", "à payer", "total TTC", "montant")
- currency: The currency code (default to "EUR" if not specified)

Optional fields:
- reference: Invoice reference number or structured communication
- dueDate: Payment due date in YYYY-MM-DD format. Look for:
  * "due date", "payment due", "pay before"
  * "échéance", "date d'échéance", "à payer avant le"
  * "vervaldatum", "betalen voor", "te betalen voor"
  * Any date that appears after these terms

Example response:
{
  "name": "ABC Company",
  "iban": "BE68539007547034",
  "amount": 123.45,
  "currency": "EUR",
  "reference": "INV-2024-001",
  "dueDate": "2024-03-15"
}

Extract ONLY these fields and return ONLY the JSON object, no other text.
For dates, convert any format to YYYY-MM-DD.`
    }, {
      role: "user" as const,
      content: `Extract payment information from this invoice text:\n\n${text}`
    }];

    // Try OpenAI extraction first
    try {
      console.log('Starting OpenAI extraction...');
      const response = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages,
        temperature: 0.1,
        max_tokens: 500
      });

      const content = response.choices[0].message?.content;
      
      if (!content) {
        console.warn('No content in OpenAI response, falling back to regex');
        return extractInvoiceDataWithRegex(text);
      }

      try {
        const result = JSON.parse(content);
        const cleanedData = cleanInvoiceData(result);
        console.log('OpenAI extraction successful:', cleanedData);
        return cleanedData;
      } catch (parseError) {
        console.error('Failed to parse OpenAI response:', parseError);
        logOpenAIInteraction(messages, content, 'Failed to parse JSON response');
        return extractInvoiceDataWithRegex(text);
      }
    } catch (openaiError) {
      console.warn('OpenAI service unavailable, falling back to regex extraction');
      return extractInvoiceDataWithRegex(text);
    }
  } catch (error) {
    console.error('Extraction error:', error);
    return extractInvoiceDataWithRegex(text);
  }
}